import { ConfigurationError } from '@cian/peperrors/shared';

import { ModuleAsset, FileAsset, InlineAsset } from './assets.js';
import {
  EAssetType,
  type IInlineAsset,
  type IModuleAsset,
  type InlineAssetHashName,
  type IFileAsset,
  EAssetContentType,
  type AssetSrc,
} from '../../../shared/assets/index.js';
import { BASE_ERR_DOMAIN } from '../constants/errors.js';

import type { ExportsScriptElement, IAssetLoader } from './domLoader.js';
import type { IAssetsStorage } from '../../assets/index.js';

export class AssetsStorage implements IAssetsStorage {
  private readonly storage: Map<AssetSrc, ModuleAsset | FileAsset> = new Map();
  private readonly inlineStorage: Map<InlineAssetHashName, InlineAsset> = new Map();
  private readonly domLoader: IAssetLoader;

  public constructor(domLoader: IAssetLoader, ssrAssets?: IterableIterator<IFileAsset | IInlineAsset | IModuleAsset>) {
    this.domLoader = domLoader;

    if (ssrAssets) {
      for (const manifest of ssrAssets) {
        const asset = this.createAssetByManifest(manifest);

        if (asset.type === EAssetType.File) {
          asset.emitLoading();
        }
      }
    }
  }

  public getAsset(src: string) {
    return this.storage.get(src);
  }

  public createAssetByManifest(asset: IFileAsset | IInlineAsset | IModuleAsset): ModuleAsset | FileAsset | InlineAsset {
    if (asset.type === EAssetType.Inline) {
      if (!this.inlineStorage.has(asset.hash)) {
        this.inlineStorage.set(asset.hash, new InlineAsset(asset, this.domLoader));
      }

      return this.inlineStorage.get(asset.hash) as InlineAsset;
    }

    if (!this.storage.has(asset.src)) {
      this.storage.set(
        asset.src,
        asset.contentType === EAssetContentType.Script
          ? new ModuleAsset(asset as IModuleAsset, this.domLoader)
          : new FileAsset(asset, this.domLoader),
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return this.storage.get(asset.src) as ModuleAsset<any>;
  }

  public pushAsset(event: Event) {
    const el = event.target as HTMLScriptElement | ExportsScriptElement | HTMLLinkElement;

    if (!el) {
      return;
    }

    let src: string;

    if (isScriptElement(el)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      src = el.getAttribute('src')!;

      if (!src) {
        // TODO: target.dataset['mf-hash']
        return;
      }
    } else if (isLinkElement(el)) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      src = el.getAttribute('href')!;

      if (!src) {
        return;
      }
    } else {
      return;
    }

    const asset = this.storage.get(src);

    if (!asset) {
      return;
    }

    if (event.type === 'load') {
      asset.emitLoad((el as ExportsScriptElement).exports);
    } else if (event.type === 'error') {
      asset.emitError(
        new ConfigurationError({
          message: `Загрузка ассета завершилась с ошибкой "${asset.src}"`,
          domain: BASE_ERR_DOMAIN + '.assets.assets-storage.push-asset',
        }),
      );
    }
  }
}

export function isScriptElement(el: HTMLElement): el is HTMLScriptElement {
  return el.tagName.toLowerCase() === 'script';
}

export function isLinkElement(el: HTMLElement): el is HTMLLinkElement {
  return el.tagName.toLowerCase() === 'link';
}
